import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { OverflowTooltip } from '../../../shared/tooltip';
import styles from './Theoretical.module.scss';
import { NettingEngineSurvey, NettingEngineSubQuestion, NettingEngineQuestionType, setSurveyQuestionIndex, getShowSummary, ISDAProductsTradedOptions, NettingEngineAgreement, ISDANettingEngineData } from '../store';
import { getAllDropdownLists } from '../../../admin/dropdown-lists/store';
import { nettingEngineISDAAgreementOptions, counterpartyCoverageOptions } from '../Utils';
import { getNettingEntities, Lei } from '../../../admin/entity/store';
import { getAvailableSubCounterpartyTypes, getIsFetchingSubCounterpartyTypes } from '../../../admin/sub-counterparty-types/store';
import { TheoreticalSurveyQuestions } from './TheoreticalSurveyQuestions';
import { TheoreticalSurveySlider } from './TheoreticalSurveySlider';
import { TheoreticalSurveyButtons } from './TheoreticalSurveyButtons';
import { AgreementType } from '../../../admin/documents/store/types';

const { green, red } = styles;

interface TheoreticalSurveyProps {
    surveyQuestionIndex: number;
    surveyAnswers: ISDANettingEngineData;
    selectedEntity: Lei | null;
}

export const TheoreticalSurveyISDA: React.FC<TheoreticalSurveyProps> = ({
    surveyQuestionIndex,
    surveyAnswers,
    selectedEntity
}) => {
    const { counterpartyNameKnown, counterparty, agreement, governingLaw, productTypesKnown, productsTraded, counterpartyIsMultiBranch, counterpartyOffices, paymentMethod } = surveyAnswers;

    const dispatch = useAppDispatch();

    const dropdownLists = useAppSelector(getAllDropdownLists);
    const showSummary = useAppSelector(getShowSummary);
    const allEntities = useAppSelector(getNettingEntities);
    const subCounterpartyList = useAppSelector(getAvailableSubCounterpartyTypes);
    const isFetchingSubCounterparties = useAppSelector(getIsFetchingSubCounterpartyTypes);

    const setQuestionIndex = useCallback((index: number) => dispatch(setSurveyQuestionIndex(index)), [dispatch]);

    const getDropdownListOptions = useCallback((listName: string) => {
        const dropdownList = dropdownLists.find(({ name }) => name === listName);
        return dropdownList ? dropdownList.options.map(type => ({ value: type, label: type })) : [];
    }, [dropdownLists]);

    const governingLawOptions = useMemo(() => getDropdownListOptions('GoverningLaw'), [getDropdownListOptions]);
    const jurisdictionOptions = useMemo(() => getDropdownListOptions('OpinionJurisdiction'), [getDropdownListOptions]);
    const cityOptions = useMemo(() => getDropdownListOptions('City'), [getDropdownListOptions]);
    const filteredCityOptions = cityOptions.filter(({ label }) => label !== 'Other');
    const [counterpartyOfficeOptions, setCounterpartyOfficeOptions] = useState(filteredCityOptions);
    const subCounterpartyOptions = useMemo(() => subCounterpartyList.map(({ subCounterpartyName, subCounterpartyTypeId }) => ({ value: subCounterpartyTypeId!.toString(), label: subCounterpartyName })), [subCounterpartyList]);
    const entityOptions = allEntities.map(({ name, entityId }) => ({ value: entityId!.toString(), label: name }));
    const paymentMethodOptions = useMemo(() => getDropdownListOptions('PaymentMethod').filter(({ label }) => label !== 'Not applicable (2002 ISDA)'), [getDropdownListOptions]);
    const productsTradedDropdownOptions: DropdownOption[] = useMemo(() => ISDAProductsTradedOptions.map(value => ({ value, label: value })), []);
    const updatedSubCounterpartyDropdownOptions = useMemo(() => {
        const includedIdsExist = subCounterpartyList.some(({ isIncludedInOpinion }) => !isUndefined(isIncludedInOpinion));
        if (!includedIdsExist) {
            return subCounterpartyOptions!;
        }

        return subCounterpartyOptions?.map(({ value, label }) => {
            const subCounterparty = subCounterpartyList.find(({ subCounterpartyName }) => subCounterpartyName === label);
            if (!subCounterparty || isUndefined(subCounterparty.isIncludedInOpinion)) {
                return { value, label };
            }

            const colour = subCounterparty.isIncludedInOpinion ? green : red;
            return { value, label, highlightColour: colour };
        });
    }, [subCounterpartyList, subCounterpartyOptions]);

    const getDropdownValueForAnswer = useCallback((answer: string | null) => isNull(answer) ? null : { value: answer, label: answer }, []);

    const getCounterpartyAnswer = useCallback((entityId: string | null) => {
        if (!isNull(selectedEntity)) {
            return { value: selectedEntity.legalName, label: selectedEntity.legalName };
        } else {
            return isNull(entityId) ? null : { value: entityId, label: allEntities.find(entity => entity.entityId === parseInt(entityId))!.name };
        }
    }, [allEntities, selectedEntity]);

    const getSubCounterpartyAnswer = useCallback((subCounterpartyId: number | null) => {
        if (!isNull(subCounterpartyId)) {
            const answer = subCounterpartyOptions.find(({ value }) => value === subCounterpartyId.toString()) || null;
            return answer;
        }
        return null;
    }, [subCounterpartyOptions]);
    const getMultiDropdownValueForAnswer = useCallback((answer: string[] | null) => (isNull(answer) || answer.length === 0) ? null : answer.map(answer => ({ value: answer, label: answer })), []);

    const unknownSubCounterparty = 'Note: If you do not know the sub counterparty type you can leave this blank, we will work with the top level counterparty type for netting considerations and analysis.';
    const noSubCounterpartyOptions = 'Note: Please note there is no sub counterparty information configured for the selected jurisdiction and counterparty pair so this can be left blank.';
    const unknownPaymentMethod = 'Note: If the payment method is unknown you can leave this blank, we will assume the second method applies for netting considerations and analysis.';

    const subCounterpartyNote = useMemo(() => {
        if (counterparty.jurisdiction && counterparty.counterpartyClassification && !subCounterpartyOptions.length && !isFetchingSubCounterparties) {
            return noSubCounterpartyOptions;
        }
        if (!isFetchingSubCounterparties) {
            return unknownSubCounterparty;
        }
        return '';
    }, [subCounterpartyOptions, noSubCounterpartyOptions, unknownSubCounterparty, counterparty, isFetchingSubCounterparties]);

    const showCounterpartyJurisdictionQuestion = useMemo(() => surveyQuestionIndex === 1 && !!counterpartyNameKnown, [surveyQuestionIndex, counterpartyNameKnown]);
    const showSubCounterpartyQuestion = useMemo(() => surveyQuestionIndex === 2 && !isNull(counterparty.jurisdiction) && !isNull(counterparty.counterpartyClassification), [surveyQuestionIndex, counterparty]);
    const showPaymentMethodQuestion = useMemo(() => agreement === NettingEngineAgreement.ISDA_1992_MASTER, [agreement]);

    const counterpartyJurisdictionQuestion: NettingEngineSubQuestion = useMemo(() => ({
        question: 'Counterparty Jurisdiction',
        value: getDropdownValueForAnswer(counterparty.jurisdiction),
        dropdownOptions: jurisdictionOptions,
        key: 'counterparty',
        counterpartyKey: 'jurisdiction',
    }), [counterparty, getDropdownValueForAnswer, jurisdictionOptions]);

    const counterpartyQuestion: NettingEngineSurvey = useMemo(() => ({
        question: 'What is the name of your counterparty?',
        type: NettingEngineQuestionType.DROPDOWN,
        dropdownOptions: entityOptions,
        value: getCounterpartyAnswer(counterparty.entityId),
        key: 'counterparty',
        counterpartyKey: 'entityId',
        showSubQuestion: showCounterpartyJurisdictionQuestion,
        subQuestion: counterpartyJurisdictionQuestion
    }), [entityOptions, getCounterpartyAnswer, counterparty, showCounterpartyJurisdictionQuestion, counterpartyJurisdictionQuestion]);

    const unknownCounterpartyQuestion: NettingEngineSurvey = useMemo(() => ({
        question: 'What is the Jurisdiction of your counterparty?',
        type: NettingEngineQuestionType.DROPDOWN,
        value: getDropdownValueForAnswer(counterparty.jurisdiction),
        dropdownOptions: jurisdictionOptions,
        key: 'counterparty',
        counterpartyKey: 'jurisdiction'
    }), [counterparty, getDropdownValueForAnswer, jurisdictionOptions]);

    const subCounterpartyQuestion: NettingEngineSubQuestion = useMemo(() => ({
        question: 'Sub Counterparty Type',
        value: getSubCounterpartyAnswer(counterparty.subCounterpartyTypeId),
        dropdownOptions: updatedSubCounterpartyDropdownOptions,
        key: 'counterparty',
        counterpartyKey: 'subCounterpartyTypeId'
    }), [counterparty, updatedSubCounterpartyDropdownOptions, getSubCounterpartyAnswer]);

    const paymentMethodQuestion: NettingEngineSubQuestion = useMemo(() => ({
        question: 'What payment method will be selected for payment on early termination?',
        type: NettingEngineQuestionType.DROPDOWN,
        value: getDropdownValueForAnswer(paymentMethod || null),
        dropdownOptions: paymentMethodOptions,
        key: 'paymentMethod',
    }), [paymentMethod, getDropdownValueForAnswer, paymentMethodOptions]);

    const nettingEngineSurvey: NettingEngineSurvey[] = useMemo(() => [
        {
            question: 'Do you know the name of your counterparty?',
            type: NettingEngineQuestionType.BOOLEAN,
            value: counterpartyNameKnown,
            key: 'counterpartyNameKnown'
        },
        counterpartyNameKnown ? counterpartyQuestion : unknownCounterpartyQuestion,
        {
            question: 'What is the classification of your counterparty?',
            type: NettingEngineQuestionType.DROPDOWN,
            value: getDropdownValueForAnswer(counterparty.counterpartyClassification),
            dropdownOptions: counterpartyCoverageOptions,
            key: 'counterparty',
            counterpartyKey: 'counterpartyClassification',
            note: showSubCounterpartyQuestion ? subCounterpartyNote : '',
            showSubQuestion: showSubCounterpartyQuestion,
            subQuestion: subCounterpartyQuestion
        },
        {
            question: 'What agreement do you intend to put in place with your counterparty?',
            type: NettingEngineQuestionType.DROPDOWN,
            value: getDropdownValueForAnswer(agreement),
            dropdownOptions: nettingEngineISDAAgreementOptions,
            key: 'agreement',
            note: showPaymentMethodQuestion ? unknownPaymentMethod : '',
            showSubQuestion: showPaymentMethodQuestion,
            subQuestion: paymentMethodQuestion
        },
        {
            question: 'What is the governing law of your agreement?',
            type: NettingEngineQuestionType.DROPDOWN,
            value: getDropdownValueForAnswer(governingLaw),
            dropdownOptions: governingLawOptions,
            key: 'governingLaw'
        },
        {
            question: 'Do you know which product types are to be traded under the relevant agreement?',
            type: NettingEngineQuestionType.BOOLEAN,
            value: productTypesKnown,
            key: 'productTypesKnown',
            note: 'Note: if you select "No", we will return a synopsis of all products in Ark 51 from the perspective of close-out netting.'
        },
        {
            question: 'Select product types to be traded',
            type: NettingEngineQuestionType.DROPDOWN,
            dropdownOptions: productsTradedDropdownOptions,
            value: getMultiDropdownValueForAnswer(productsTraded),
            key: 'productsTraded',
            isMulti: true,
            closeOnSelect: false
        },
        {
            question: 'Is your counterparty acting on a multi-branch basis?',
            type: NettingEngineQuestionType.BOOLEAN,
            value: counterpartyIsMultiBranch,
            key: 'counterpartyIsMultiBranch',
            note: 'Note: if you select "No", it will be assumed that the counterparty is operating from an office in the jurisdiction of its incorporation.'
        },
        {
            question: 'Please provide a list of all of the offices through which your counterparty may act.',
            type: NettingEngineQuestionType.DROPDOWN,
            dropdownOptions: counterpartyOfficeOptions,
            value: getMultiDropdownValueForAnswer(counterpartyOffices),
            key: 'counterpartyOffices',
            isMulti: true,
            closeOnSelect: false
        },
    ], [counterpartyNameKnown, counterpartyQuestion, unknownCounterpartyQuestion, getDropdownValueForAnswer, counterparty.counterpartyClassification, showSubCounterpartyQuestion, subCounterpartyNote, subCounterpartyQuestion, agreement, showPaymentMethodQuestion, paymentMethodQuestion, governingLaw, governingLawOptions, productTypesKnown, productsTradedDropdownOptions, getMultiDropdownValueForAnswer, productsTraded, counterpartyIsMultiBranch, counterpartyOfficeOptions, counterpartyOffices]);

    const getSummaryAnswer = useCallback((answer: boolean | DropdownOption | DropdownOption[] | null, type: NettingEngineQuestionType, isMulti?: boolean, question?: string) => {
        if (isNull(answer)) return '';
        if (type === NettingEngineQuestionType.BOOLEAN) return !answer ? 'No' : 'Yes';
        if (isMulti) return (answer as DropdownOption[]).map(({ label }) => label);
        if (question === 'What is the classification of your counterparty?') {
            return `${(answer as DropdownOption).label}${isNull(getSubCounterpartyAnswer(counterparty.subCounterpartyTypeId)) ? '' : ` - ${getSubCounterpartyAnswer(counterparty.subCounterpartyTypeId)!.label}`}`;

        }
        return (answer as DropdownOption).label;
    }, [getSubCounterpartyAnswer, counterparty.subCounterpartyTypeId]);

    const updateCounterpartyOfficeOptions = useCallback((options: DropdownOption[]) => setCounterpartyOfficeOptions(options), []);

    return (
        <>
            <div className={styles.surveyWrapper}>
                {!showSummary &&
                    <>
                        <TheoreticalSurveySlider
                            surveyQuestionIndex={surveyQuestionIndex}
                            nettingEngineSurvey={nettingEngineSurvey}
                            setQuestionIndex={setQuestionIndex}
                        />
                        <TheoreticalSurveyQuestions
                            surveyQuestionIndex={surveyQuestionIndex}
                            nettingEngineSurvey={nettingEngineSurvey}
                            allEntities={allEntities}
                            setQuestionIndex={setQuestionIndex}
                            counterpartyNameKnown={counterpartyNameKnown}
                            jurisdictionOptions={jurisdictionOptions}
                            updateCounterpartyOfficeOptions={updateCounterpartyOfficeOptions}
                            agreementType={AgreementType.ISDA}
                        />
                    </>
                }
                {showSummary &&
                    <div className={styles.surveySummaryWrapper}>
                        <div className={styles.summaryTitle}>Summary</div>
                        {nettingEngineSurvey.map(({ question, value, type, isMulti }, index) => {
                            if (isNull(value)) {
                                return null;
                            }
                            return (
                                <div className={styles.surveySummary} key={index}>
                                    <OverflowTooltip className={styles.surveySummaryQuestion} overlayText={`Q: ${question}`} />
                                    <OverflowTooltip className={styles.surveySummaryAnswer} overlayText={`A: ${getSummaryAnswer(value, type, isMulti, question)}`} />
                                </div>
                            );
                        })}
                    </div>
                }
            </div>
            <TheoreticalSurveyButtons
                surveyQuestionIndex={surveyQuestionIndex}
                nettingEngineSurvey={nettingEngineSurvey}
                setQuestionIndex={setQuestionIndex}
            />
        </>
    );
};
